import { Link } from 'react-router-dom';

export default function TermsOfService() {
  return (
    <div className="legal-page">
      <Link to="/" className="back-link">&larr; Back</Link>
      <h1>Terms of Service</h1>
      <p className="legal-date">Effective Date: March 30, 2026</p>

      <p>
        These Terms of Service ("Terms") govern your use of the MileDeduct mobile application and website
        (collectively, the "Service"), operated by MileDeduct ("we," "us," or "our"). By accessing or using
        the Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 18 years old and capable of forming a binding contract to use the Service.
        By using the Service, you represent and warrant that you meet these requirements.
      </p>

      <h2>2. Account Registration</h2>
      <p>
        To access certain features, you must create an account using a valid email address or a third-party
        authentication provider (e.g., Google Sign-In). You are responsible for maintaining the confidentiality
        of your account credentials and for all activities that occur under your account. You agree to notify
        us immediately of any unauthorized use of your account.
      </p>

      <h2>3. Description of Service</h2>
      <p>
        MileDeduct provides automatic and manual mileage tracking, IRS standard mileage rate calculations,
        trip logging, and report generation for personal tax record-keeping purposes. The Service is designed
        to assist users in tracking business mileage for potential tax deductions.
      </p>

      <h2>4. Subscription and Payment</h2>
      <p>
        Access to the full Service requires a paid subscription. Subscription pricing, billing frequency,
        and features are described at the time of purchase. Subscriptions automatically renew unless canceled
        before the end of the current billing period.
      </p>
      <ul>
        <li>All fees are charged in U.S. dollars.</li>
        <li>Payments are processed through Stripe, a third-party payment processor. By subscribing, you agree to Stripe's terms of service.</li>
        <li>All subscription fees are non-refundable. You may cancel your subscription at any time, but no refunds or credits will be issued for partial billing periods. A 30-day free trial is provided for new subscribers to evaluate the Service before being charged.</li>
        <li>We reserve the right to change subscription pricing with 30 days' advance notice.</li>
      </ul>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
        <li>Attempt to gain unauthorized access to the Service, other accounts, or computer systems.</li>
        <li>Interfere with or disrupt the integrity or performance of the Service.</li>
        <li>Reverse engineer, decompile, or disassemble any portion of the Service.</li>
        <li>Use the Service to generate fraudulent mileage records or tax documents.</li>
        <li>Resell, redistribute, or sublicense access to the Service without our written consent.</li>
      </ul>

      <h2>6. Tax Disclaimer</h2>
      <p>
        <strong>MileDeduct is not a tax advisor, CPA, or tax preparation service.</strong> The Service provides
        mileage tracking tools and estimated deduction calculations based on publicly available IRS standard
        mileage rates. All calculations are estimates and should not be construed as tax advice. You are solely
        responsible for verifying the accuracy of your mileage records and consulting a qualified tax professional
        regarding your specific tax situation. We make no representations or warranties regarding the tax
        deductibility of any mileage tracked through the Service.
      </p>

      <h2>7. IRS Mileage Rates</h2>
      <p>
        The Service uses IRS standard mileage rates published by the Internal Revenue Service. While we make
        reasonable efforts to update these rates promptly when they change, we do not guarantee that the rates
        displayed in the Service are current or accurate at all times. Users should independently verify
        applicable IRS rates.
      </p>

      <h2>8. Data and Content</h2>
      <p>
        You retain ownership of all data you input into the Service, including trip records, vehicle information,
        and personal details ("User Data"). By using the Service, you grant us a limited license to store,
        process, and display your User Data solely for the purpose of providing the Service to you.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        The Service, including its design, logos, trademarks, code, and content (excluding User Data), is
        owned by MileDeduct and protected by intellectual property laws. You may not copy, modify, distribute,
        or create derivative works based on the Service without our prior written consent.
      </p>

      <h2>10. Third-Party Services</h2>
      <p>
        The Service integrates with third-party services including Google Maps Platform, Firebase (Google),
        and Stripe. Your use of these integrations is subject to the respective third-party terms of service
        and privacy policies. We are not responsible for the availability, accuracy, or practices of
        third-party services.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, MILEDEDUCT AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
        AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN
        CONNECTION WITH YOUR USE OF THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
        NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES.
      </p>
      <p>
        OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED THE
        AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
      </p>

      <h2>12. Disclaimer of Warranties</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
        OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
        ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless MileDeduct and its affiliates from any claims,
        liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of
        or in connection with your use of the Service, your violation of these Terms, or your violation
        of any rights of a third party.
      </p>

      <h2>14. Termination</h2>
      <p>
        We may suspend or terminate your access to the Service at any time, with or without cause, and
        with or without notice. Upon termination, your right to use the Service ceases immediately.
        You may request export of your data within 30 days of termination by contacting
        support@milededuct.app.
      </p>

      <h2>15. Modifications to Terms</h2>
      <p>
        We reserve the right to modify these Terms at any time. We will notify you of material changes
        by posting the updated Terms on the Service and updating the "Effective Date" above. Your continued
        use of the Service after such changes constitutes acceptance of the modified Terms.
      </p>

      <h2>16. Governing Law and Dispute Resolution</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State of Delaware,
        United States, without regard to its conflict of law provisions. Any disputes arising out of or
        relating to these Terms or the Service shall be resolved through binding arbitration administered
        by the American Arbitration Association, except that either party may seek injunctive relief in
        any court of competent jurisdiction.
      </p>

      <h2>17. Severability</h2>
      <p>
        If any provision of these Terms is held to be unenforceable, the remaining provisions shall
        continue in full force and effect.
      </p>

      <h2>18. Entire Agreement</h2>
      <p>
        These Terms, together with our <Link to="/privacy">Privacy Policy</Link>, constitute the entire
        agreement between you and MileDeduct regarding your use of the Service and supersede any prior
        agreements.
      </p>

      <h2>19. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at:<br />
        <strong>support@milededuct.app</strong>
      </p>
    </div>
  );
}
